import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, RotateCcw, Activity } from 'lucide-react';
import { toast } from 'sonner';

export const ShareExpandedDetail = ({ share, activityLogs, extendDays, setExtendDays, revokeConfirm, setRevokeConfirm, handleExtend, handleRevoke, formatDate, getShareUrl }) => {
  const copyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getShareUrl(share.access_token));
    toast.success('Link copied to clipboard.');
  };

  return (
    <div className="border-t bg-slate-50 p-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div><span className="text-slate-500 text-xs">Created</span><p className="font-medium">{formatDate(share.created_at)}</p></div>
        <div><span className="text-slate-500 text-xs">Expires</span><p className="font-medium">{formatDate(share.expires_at)}</p></div>
        <div><span className="text-slate-500 text-xs">Branding</span><p className="font-medium">{share.branding === 'myinvestfolio' ? 'MyInvestFolio' : 'White-label'}</p></div>
        <div><span className="text-slate-500 text-xs">PDF View</span><p className="font-medium">{share.permissions?.allow_pdf_view ? 'Allowed' : 'No'}</p></div>
        <div><span className="text-slate-500 text-xs">Summary Export</span><p className="font-medium">{share.permissions?.allow_summary_export ? 'Allowed' : 'No'}</p></div>
      </div>

      {share.status === 'active' && (
        <div className="p-3 bg-white rounded-lg border">
          <Label className="text-xs text-slate-500 mb-1 block">Share Link</Label>
          <div className="flex gap-2">
            <Input readOnly value={getShareUrl(share.access_token)} className="text-xs bg-slate-50 font-mono" data-testid={`share-link-${share.share_id}`} />
            <Button variant="outline" size="sm" data-testid={`share-copy-${share.share_id}`} onClick={copyLink}>Copy</Button>
          </div>
        </div>
      )}

      {share.status !== 'revoked' && (
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Extend by</Label>
              <Select value={String(extendDays[share.share_id] || '7')} onValueChange={v => setExtendDays(prev => ({ ...prev, [share.share_id]: v }))}>
                <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Day</SelectItem><SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem><SelectItem value="30">30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleExtend(share.share_id); }} data-testid={`share-extend-${share.share_id}`}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Extend
            </Button>
          </div>
          {revokeConfirm === share.share_id ? (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-red-600">Access will be terminated immediately.</span>
              <Button variant="destructive" size="sm" onClick={(e) => { e.stopPropagation(); handleRevoke(share.share_id); }} data-testid={`share-revoke-confirm-${share.share_id}`}>Confirm Revoke</Button>
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setRevokeConfirm(null); }}>Cancel</Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 ml-auto" onClick={(e) => { e.stopPropagation(); setRevokeConfirm(share.share_id); }} data-testid={`share-revoke-${share.share_id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
            </Button>
          )}
        </div>
      )}

      <div>
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Activity className="w-4 h-4" /> Activity Log</h4>
        {activityLogs[share.share_id]?.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100"><tr><th className="text-left p-2 font-medium text-slate-600">Timestamp</th><th className="text-left p-2 font-medium text-slate-600">Action</th><th className="text-left p-2 font-medium text-slate-600 hidden sm:table-cell">Page</th></tr></thead>
              <tbody>
                {activityLogs[share.share_id].map((log, i) => (
                  <tr key={log.log_id || i} className="border-t"><td className="p-2 text-slate-700">{formatDate(log.timestamp)}</td><td className="p-2 text-slate-700 capitalize">{log.action}</td><td className="p-2 text-slate-500 hidden sm:table-cell">{log.page_viewed || '\u2014'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-slate-400">No activity recorded yet.</p>
        )}
      </div>
    </div>
  );
};
